import React, { useCallback, useImperativeHandle, useRef } from "react";
import {
  CardActions3D,
  CardActionsImage,
  CardActionsSkybox,
  CardActionsTexture,
  CardActionsVideo,
} from "domains/assets/components/CardActions";
import {
  ListActions3D,
  ListActionsImage,
  ListActionsSkybox,
  ListActionsTexture,
  ListActionsVideo,
} from "domains/assets/components/ListActions";
import useAllAssetsAdvanced from "domains/assets/hooks/useAllAssetsAdvanced";
import useAllPublicAssets from "domains/assets/hooks/useAllPublicAssets";
import useAssetJobFilterAuthor from "domains/assets/hooks/useAssetAuthor";
import useAssetFilterCollection from "domains/assets/hooks/useAssetFilterCollection";
import useAssetFilterDate from "domains/assets/hooks/useAssetFilterDate";
import useAssetFilterModels from "domains/assets/hooks/useAssetFilterModels";
import useAssetFilterTags from "domains/assets/hooks/useAssetFilterTags";
import useAssetFilterType from "domains/assets/hooks/useAssetFilterType";
import useAssetSearch from "domains/assets/hooks/useAssetSearch";
import useAssetSort from "domains/assets/hooks/useAssetSort";
import useAssetView from "domains/assets/hooks/useAssetView";
import CanvasListEmptyState from "domains/canvas/components/CanvasListEmptyState";
import useAllCanvasAdvanced from "domains/canvas/hooks/useAllCanvasAdvanced";
import { assertNever } from "domains/commons/assertNever";
import { ExploreTabId } from "domains/explore/constants";
import FileManagerCanvas from "domains/file-manager/components/FileManagerCanvas";
import FileManagerImage from "domains/file-manager/components/FileManagerImage";
import EmptyMessage, {
  EmptyMessage3D,
  EmptyMessageSkybox,
  EmptyMessageTexture,
  EmptyMessageVideo,
} from "domains/file-manager/components/FileManagerImage/EmptyMessage";
import FileManagerModel from "domains/file-manager/components/FileManagerModel";
import { FmFileImage } from "domains/file-manager/interfacesV2";
import ModelListEmptyState from "domains/models/components/ModelList/ModelListEmptyState";
import useAllModelsWithFilters from "domains/models/hooks/useAllModelsWithFilters";
import useAllPublicModels from "domains/models/hooks/useAllPublicModels";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import { extraTheme } from "domains/theme";
import Button, { ButtonProps } from "domains/ui/components/Button";
import Icon from "domains/ui/components/Icon";
import { useItemTabIndicator } from "domains/ui/hooks/useItemTabIndicator";
import { useUserContext } from "domains/user/contexts/UserProvider";
import { AnalyticsEvents } from "infra/analytics/constants/Events";
import Track from "infra/analytics/Track";

import { Box, HStack, VStack } from "@chakra-ui/react";

export interface ExploreListHandle {
  reset: () => void;
  goToUploaded: () => void;
}

interface ExploreListProps {
  context: "home" | "explore" | "library";
  isWithHeader?: boolean;
  tabId: ExploreTabId;
  collectionId?: string;
  initialIndex?: number;
  onInitialFirstRender?: () => void;
  onTabChange: (tabId: ExploreTabId) => void;
  onCardClick: (file: FmFileImage, index: number) => void;
  handleRef?: React.Ref<ExploreListHandle>;
}

export default React.memo(function ExploreList({
  context,
  isWithHeader = false,
  tabId,
  collectionId,
  initialIndex,
  onInitialFirstRender,
  onTabChange,
  onCardClick,
  handleRef,
}: ExploreListProps) {
  const { hasNoProject, isReader } = useTeamContext();
  const { isUnsigned } = useUserContext();
  const navbarRef = useRef<HTMLDivElement>(null);
  const tabIndicatorRef = useRef<HTMLDivElement>(null);

  const isFmImage = tabId !== "model" && tabId !== "canvas";
  const modelFilterType =
    tabId === "image" ||
    tabId === "3d" ||
    tabId === "video" ||
    tabId === "texture"
      ? tabId
      : undefined;
  const assetType = isFmImage ? tabId : "image";
  const fmViewProps = useAssetView({
    ...(context !== "library" && { options: ["masonry", "fill", "fit"] }),
    ...(isFmImage && { variant: tabId }),
  });

  const {
    allAssetsTypeArgs: assetsTypesQueryArgs,
    fmHeaderFilterAssetProps,
    goToUploaded,
    reset,
  } = useAssetFilterType({
    assetType,
    excludedOptions:
      context !== "library"
        ? [
            "image:canvasExport",
            "image:uploaded",
            "video:uploaded",
            "image:detection",
            "skybox:inference",
            "texture:inference",
            "texture:textureMaps",
            "texture:upscaled", //////////// Recreate button is useless here, we have to retrieve the initial texture to be able to recreate the asset
          ]
        : undefined,
    excludeUpload: context !== "library",
    showVectorization: true,
    avoidPersistedState: true,
  });
  const { searchQueryArgs, fmHeaderSearchProps } = useAssetSearch();
  const { authorQueryArgs, fmHeaderFilterAuthorProps } =
    useAssetJobFilterAuthor();
  const { dateQueryArgs, fmHeaderFilterDateProps } = useAssetFilterDate();
  const { tagsQueryArgs, fmHeaderFilterTagsProps } = useAssetFilterTags();
  const { modelsQueryArgs, fmHeaderFilterModelsProps } = useAssetFilterModels({
    privacy: context === "library" ? "private" : "public",
    isEnabled:
      !!modelFilterType && !(context !== "library" && tabId === "texture"),
    variant: modelFilterType,
  });
  const { sortQueryArgs, fmHeaderSortProps } = useAssetSort();
  const { allAssetsCollectionArgs, fmHeaderCollectionProps } =
    useAssetFilterCollection();

  const isExploreAssets = isFmImage && context !== "library";
  const isLibraryAssets = isFmImage && context === "library";

  const isPublicAssets = isFmImage && isUnsigned;
  const publicAssetsResult = useAllPublicAssets(
    isPublicAssets
      ? {
          ...assetsTypesQueryArgs,
        }
      : undefined
  );

  const isPrivateAssets = isFmImage && !isUnsigned;
  const privateAssetsResult = useAllAssetsAdvanced(
    isPrivateAssets
      ? {
          ...(context !== "home" && {
            ...searchQueryArgs,
            ...tagsQueryArgs,
            ...modelsQueryArgs,
          }),
          ...assetsTypesQueryArgs,
          ...(context === "library"
            ? {
                ...(collectionId ? { collectionIds: [collectionId] } : {}),
                ...(!collectionId && allAssetsCollectionArgs.collectionId
                  ? { collectionIds: [allAssetsCollectionArgs.collectionId] }
                  : {}),
                ...sortQueryArgs,
                ...dateQueryArgs,
                ...authorQueryArgs,
              }
            : {
                privacy: "public",
                sortBy: "updatedAt",
                sortDirection: "desc",
              }),
        }
      : undefined
  );

  const AssetEmptyState =
    (tabId === "skybox" && EmptyMessageSkybox) ||
    (tabId === "texture" && EmptyMessageTexture) ||
    (tabId === "3d" && EmptyMessage3D) ||
    (tabId === "video" && EmptyMessageVideo) ||
    EmptyMessage;

  const AssetCardActions =
    (tabId === "skybox" && CardActionsSkybox) ||
    (tabId === "texture" && CardActionsTexture) ||
    (tabId === "3d" && CardActions3D) ||
    (tabId === "video" && CardActionsVideo) ||
    CardActionsImage;

  const AssetListActions =
    (tabId === "skybox" && ListActionsSkybox) ||
    (tabId === "texture" && ListActionsTexture) ||
    (tabId === "3d" && ListActions3D) ||
    (tabId === "video" && ListActionsVideo) ||
    ListActionsImage;

  // ----------------------------------

  const isPrivateCanvas = tabId === "canvas" && context === "library";
  const privateCanvasResult = useAllCanvasAdvanced(
    isPrivateCanvas
      ? {
          ...searchQueryArgs,
          ...sortQueryArgs,
          ...(collectionId ? { collectionIds: [collectionId] } : {}),
          ...(!collectionId && allAssetsCollectionArgs.collectionId
            ? { collectionIds: [allAssetsCollectionArgs.collectionId] }
            : {}),
          ...dateQueryArgs,
          ...authorQueryArgs,
        }
      : undefined
  );

  // ----------------------------------

  const isPrivateModels = tabId === "model" && !isUnsigned;
  const privateModelsResult = useAllModelsWithFilters(
    isPrivateModels
      ? {
          isIgnoringFilters: context === "home",
          isFeaturedPrioritized: true,
          sortDirection: sortQueryArgs.sortDirection as "asc" | "desc",
          ...(context === "library"
            ? {
                collectionId,
              }
            : { privacyFilter: "public" }),
        }
      : undefined
  );

  const isPublicModels = tabId === "model" && isUnsigned;
  const publicModelsResult = useAllPublicModels(
    isPublicModels ? {} : undefined
  );

  // ----------------------------------

  const handleTabChange = useCallback(
    (tabId: ExploreTabId) => {
      let event: string | undefined;
      switch (tabId) {
        case "image":
          event = AnalyticsEvents.Navigation.ClickedSelectImages;
          break;
        case "model":
          event = AnalyticsEvents.Navigation.ClickedSelectModels;
          break;
        case "video":
          event = AnalyticsEvents.Navigation.ClickedSelectVideos;
          break;
        case "3d":
          event = AnalyticsEvents.Navigation.ClickedSelect3D;
          break;
        case "texture":
          event = AnalyticsEvents.Navigation.ClickedSelectTextures;
          break;
        case "skybox":
          event = AnalyticsEvents.Navigation.ClickedSelectSkyboxes;
          break;
        case "canvas":
          event = AnalyticsEvents.Navigation.ClickedSelectCanvas;
          break;
        default:
          assertNever(tabId);
      }
      if (event) {
        Track(event, {
          persona: "default",
          context,
          tabId,
        });
      }
      onTabChange?.(tabId);
    },
    [onTabChange, context]
  );

  // ----------------------------------

  useImperativeHandle(
    handleRef,
    () => ({
      goToUploaded,
      reset,
    }),
    [goToUploaded, reset]
  );

  // ----------------------------------

  useItemTabIndicator({
    containerRef: navbarRef,
    tabIndicatorRef: tabIndicatorRef,
    activeItemDataId: `explore-tab-${tabId}`,
  });

  return (
    <VStack align="stretch" spacing={6}>
      <HStack
        ref={navbarRef}
        pos="relative"
        alignSelf="center"
        p={2}
        borderRadius="xl"
        bgColor="backgroundTertiary.500"
        spacing={2}
      >
        <Box
          ref={tabIndicatorRef}
          pos="absolute"
          borderRadius="md"
          transition={extraTheme.transitions.fast}
          bgColor="textPrimary"
        />

        <ExploreTab
          id="image"
          tooltip="Images"
          onClick={() => handleTabChange("image")}
          isActive={tabId === "image"}
        >
          <Icon id="Nav/Image/Outline" h="16px" />
        </ExploreTab>

        <ExploreTab
          id="video"
          tooltip="Videos"
          onClick={() => handleTabChange("video")}
          isActive={tabId === "video"}
        >
          <Icon id="Nav/Video/Outline" h="14px" />
        </ExploreTab>

        <ExploreTab
          id="3d"
          tooltip="3D"
          onClick={() => handleTabChange("3d")}
          isActive={tabId === "3d"}
        >
          <Icon id="Nav/3D/Outline" h="18px" />
        </ExploreTab>

        <ExploreTab
          id="texture"
          tooltip="Textures"
          onClick={() => handleTabChange("texture")}
          isActive={tabId === "texture"}
        >
          <Icon id="Nav/Texture/Outline" h="16px" />
        </ExploreTab>

        <ExploreTab
          id="skybox"
          tooltip="Skyboxes"
          onClick={() => handleTabChange("skybox")}
          isActive={tabId === "skybox"}
        >
          <Icon id="Nav/Skybox/Outline" h="16px" />
        </ExploreTab>

        {context === "library" && (
          <ExploreTab
            id="canvas"
            tooltip="Canvas"
            onClick={() => handleTabChange("canvas")}
            isActive={tabId === "canvas"}
          >
            <Icon id="Nav/Canvas/Outline" h="16px" />
          </ExploreTab>
        )}

        <ExploreTab
          id="model"
          tooltip="Models"
          onClick={() => handleTabChange("model")}
          isActive={tabId === "model"}
        >
          <Icon id="Nav/Model/Outline" h="16px" />
        </ExploreTab>
      </HStack>

      <Box w="full" minH="400px">
        {isExploreAssets && (
          <FileManagerImage
            key={tabId}
            files={
              isPublicAssets
                ? publicAssetsResult.files
                : privateAssetsResult.files
            }
            isLoading={
              isPublicAssets
                ? publicAssetsResult.isLoading
                : privateAssetsResult.isLoading
            }
            hasMore={(() => {
              if (context === "home") {
                return undefined;
              } else if (isPublicAssets) {
                return publicAssetsResult.hasMore;
              } else {
                return privateAssetsResult.hasMore;
              }
            })()}
            onEndReached={(() => {
              if (context === "home") {
                return undefined;
              } else if (isPublicAssets) {
                return publicAssetsResult.loadMore;
              } else {
                return privateAssetsResult.loadMore;
              }
            })()}
            isWithoutAssetZoom
            isWithoutHeader={!isWithHeader}
            isWithoutSelectionBar
            isCardWithoutActions
            isReloadEnabled={isUnsigned || !hasNoProject}
            headerFilterAssetProps={fmHeaderFilterAssetProps}
            {...(context === "explore" && {
              headerSearchProps: fmHeaderSearchProps,
              headerFilterTagsProps: fmHeaderFilterTagsProps,
              headerFilterModelsProps: fmHeaderFilterModelsProps,
              headerSortProps: fmHeaderSortProps,
            })}
            onCardClick={onCardClick}
            withLink={false}
            {...(isWithHeader ? fmViewProps : { view: "masonry" })}
            variant={tabId === "video" ? "image" : tabId}
            minColumnsCount={3}
            initialIndex={initialIndex}
            onInitialFirstRender={onInitialFirstRender}
            columnsCount={isWithHeader ? undefined : 4}
            EmptyStateComponent={AssetEmptyState}
          />
        )}

        {isLibraryAssets && (
          <FileManagerImage
            key={tabId}
            files={
              fmViewProps.view === "jobs"
                ? undefined
                : privateAssetsResult.files
            }
            jobs={
              fmViewProps.view === "jobs" ? privateAssetsResult.jobs : undefined
            }
            isSelectable
            isLoading={privateAssetsResult.isLoading}
            hasMore={privateAssetsResult.hasMore}
            onEndReached={privateAssetsResult.loadMore}
            isReloadEnabled={!hasNoProject}
            withLink={false}
            isCardWithoutActions={isReader}
            {...fmViewProps}
            headerFilterAssetProps={fmHeaderFilterAssetProps}
            headerSearchProps={fmHeaderSearchProps}
            headerFilterTagsProps={fmHeaderFilterTagsProps}
            headerFilterModelsProps={fmHeaderFilterModelsProps}
            headerSortProps={fmHeaderSortProps}
            headerFilterAuthorProps={fmHeaderFilterAuthorProps}
            headerFilterCollectionProps={
              !collectionId ? fmHeaderCollectionProps : undefined
            }
            headerFilterDateProps={fmHeaderFilterDateProps}
            variant={tabId === "video" ? "image" : tabId}
            minColumnsCount={3}
            EmptyStateComponent={AssetEmptyState}
            CardActionsComponent={AssetCardActions}
            ListActionsComponent={AssetListActions}
          />
        )}

        {isPrivateCanvas && (
          <FileManagerCanvas
            files={privateCanvasResult.files}
            isLoading={privateCanvasResult.isLoading}
            hasMore={privateCanvasResult.hasMore}
            onEndReached={privateCanvasResult.loadMore}
            isSelectable
            withLink={false}
            headerSearchProps={fmHeaderSearchProps}
            headerFilterAuthorProps={
              collectionId ? undefined : fmHeaderFilterAuthorProps
            }
            headerFilterCollectionProps={
              !collectionId ? fmHeaderCollectionProps : undefined
            }
            headerFilterDateProps={fmHeaderFilterDateProps}
            headerSortProps={fmHeaderSortProps}
            minColumnsCount={3}
            EmptyStateComponent={CanvasListEmptyState}
          />
        )}

        {isPublicModels && (
          <FileManagerModel
            files={publicModelsResult.files}
            isLoading={publicModelsResult.isLoading}
            hasMore={
              context !== "home" ? publicModelsResult.hasMore : undefined
            }
            onEndReached={
              context !== "home" ? publicModelsResult.loadMore : undefined
            }
            isWithoutHeader={!isWithHeader}
            isWithoutSelectionBar
            withLink={false}
            minColumnsCount={3}
            columnsCount={isWithHeader ? undefined : 4}
            EmptyStateComponent={ModelListEmptyState}
          />
        )}

        {isPrivateModels && (
          <FileManagerModel
            files={privateModelsResult.files}
            isLoading={privateModelsResult.isLoading}
            hasMore={privateModelsResult.hasMore}
            onEndReached={privateModelsResult.loadMore}
            isSelectable={!privateModelsResult.isMultiSelectDisabled}
            withLink={false}
            minColumnsCount={3}
            EmptyStateComponent={ModelListEmptyState}
            emptyStateProps={{
              reason: privateModelsResult.filterCount
                ? "no-search-results"
                : "no-models",
            }}
            columnsCount={isWithHeader ? undefined : 4}
            headerSortProps={fmHeaderSortProps}
            isWithoutHeader={!isWithHeader}
            headerFilterContent={privateModelsResult.topBarFilterContent}
            filterCount={privateModelsResult.filterCount}
          />
        )}
      </Box>
    </VStack>
  );
});

// ------------------------------------

interface ExploreTabProps extends Omit<ButtonProps, "isActive"> {
  id: string;
  isActive?: boolean;
}

function ExploreTab({ id, isActive = false, ...props }: ExploreTabProps) {
  const dataId = `explore-tab-${id}`;
  return (
    <Button
      size="sm"
      px={4}
      h="40px"
      variant={isActive ? "primary" : "filterGhost"}
      colorScheme={isActive ? "white" : undefined}
      bgColor={isActive ? "transparent !important" : undefined}
      data-id={dataId}
      {...props}
    />
  );
}
